import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { TabState, DatasetId, ReportId, ModelId, getFromDatasetRecord, createDatasetId, createReportId, createModelId } from '../../../../types';
import { MIDASProject, Report } from '../../../../types';
import { DerivedDataSet } from '../../../../types/dataset.types';
import { ModelManager } from '../../../utils/ModelManager';
import { checkDatasetDependencies, generateDependencyWarningMessage } from '../../../utils/dependencyChecker';
import logger from '../../../utils/logger';
import * as styles from './ProjectOverviewTabContent.css.ts';
import { useProjectStore } from '../../../stores/projectStore';
import { usePaneLayout } from '../../../stores/hooks/usePaneLayout';
import { useNotificationStore } from '../../../stores/notificationStore';
import { useConfirmDialogStore } from '../../../stores/confirmDialogStore';
import { createDataTableTab, createReportTab, createModelDetailTab } from '../../../tab-system/TabRegistry';
import { DatasetMetadataModal } from '../../DatasetMetadataModal';
import { duckdbProxy } from '../../../utils/duckdb';

interface ProjectOverviewTabContentProps {
  tab: TabState;
}

export const ProjectOverviewTabContent: React.FC<ProjectOverviewTabContentProps> = () => {
  // Hooksを使用してデータと関数を取得
  const project = useProjectStore(state => state.project);
  const updateProject = useProjectStore(state => state.updateProject);
  const updateDataset = useProjectStore(state => state.updateDataset);
  const updateDatasetName = useProjectStore(state => state.updateDatasetName);
  const { addTab, getActiveContainer } = usePaneLayout();
  const { error: showError } = useNotificationStore();
  const confirm = useConfirmDialogStore(state => state.confirm);

  // プロジェクト名の編集状態
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [projectNameInput, setProjectNameInput] = useState(project?.projectName || '');

  // データセットメニューの状態
  const [openMenuDatasetId, setOpenMenuDatasetId] = useState<string | null>(null);

  // データセット名の編集状態
  const [editingDatasetId, setEditingDatasetId] = useState<string | null>(null);
  const [datasetNameInput, setDatasetNameInput] = useState('');

  // メタデータモーダルの状態
  const [metadataDatasetId, setMetadataDatasetId] = useState<string | null>(null);
  
  // タブを開く関数を定義
  const onOpenDataset = useCallback((datasetId: DatasetId) => {
    if (!project?.datasets) return;
    const dataset = getFromDatasetRecord(project.datasets, datasetId);
    if (!dataset) return;
    
    const dataTableTab = createDataTableTab(datasetId, dataset.name);
    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, dataTableTab, { activatePane: true });
    }
  }, [project, addTab, getActiveContainer]);
  
  const onOpenReport = useCallback((reportId: ReportId) => {
    const report = project?.reports?.find(r => r.id === reportId);
    if (!report) return;
    
    const reportTab = createReportTab(reportId, report.name);
    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, reportTab, { activatePane: true });
    }
  }, [project, addTab, getActiveContainer]);
  
  const onOpenModel = useCallback((modelId: ModelId) => {
    if (!project) return;
    
    const modelManager = new ModelManager(project);
    const model = modelManager.getModel(modelId);
    if (!model) return;
    
    const modelTab = createModelDetailTab(modelId, model.name);
    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, modelTab, { activatePane: true });
    }
  }, [project, addTab, getActiveContainer]);
  
  const onProjectUpdate = useCallback((updatedProject: MIDASProject) => {
    updateProject(() => updatedProject);
  }, [updateProject]);

  // プロジェクト名の編集開始
  const handleStartEditProjectName = useCallback(() => {
    if (!project) return;
    setProjectNameInput(project.projectName);
    setIsEditingProjectName(true);
  }, [project]);

  // プロジェクト名の保存
  const handleSaveProjectName = useCallback(() => {
    if (!project) return;
    const trimmedName = projectNameInput.trim();
    if (trimmedName === '') {
      logger.warn('[ProjectOverviewTabContent] Project name cannot be empty');
      setProjectNameInput(project.projectName);
      setIsEditingProjectName(false);
      return;
    }
    if (trimmedName !== project.projectName) {
      logger.info('[ProjectOverviewTabContent] Updating project name', {
        oldName: project.projectName,
        newName: trimmedName
      });
      const updatedProject = {
        ...project,
        projectName: trimmedName,
        metadata: {
          ...project.metadata,
          lastModified: new Date().toISOString()
        }
      };
      onProjectUpdate(updatedProject);
    }
    setIsEditingProjectName(false);
  }, [project, projectNameInput, onProjectUpdate]);

  // プロジェクト名の編集キャンセル
  const handleCancelEditProjectName = useCallback(() => {
    if (!project) return;
    setProjectNameInput(project.projectName);
    setIsEditingProjectName(false);
  }, [project]);

  // プロジェクト名編集のキーハンドラー
  const handleProjectNameKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSaveProjectName();
    } else if (event.key === 'Escape') {
      handleCancelEditProjectName();
    }
  }, [handleSaveProjectName, handleCancelEditProjectName]);

  // メニュー開閉ハンドラー
  const handleToggleMenu = useCallback((datasetId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuDatasetId(openMenuDatasetId === datasetId ? null : datasetId);
  }, [openMenuDatasetId]);

  // メニューを閉じる
  const handleCloseMenu = useCallback(() => {
    setOpenMenuDatasetId(null);
  }, []);

  // データセット名の編集開始
  const handleStartEditDatasetName = useCallback((datasetId: string, currentName: string) => {
    setEditingDatasetId(datasetId);
    setDatasetNameInput(currentName);
    setOpenMenuDatasetId(null); // メニューを閉じる
  }, []);

  // データセット名の保存
  const handleSaveDatasetName = useCallback((datasetId: string) => {
    if (!project) return;

    const trimmedName = datasetNameInput.trim();
    if (trimmedName === '') {
      logger.warn('[ProjectOverviewTabContent] Dataset name cannot be empty');
      const dataset = getFromDatasetRecord(project.datasets, createDatasetId(datasetId));
      if (dataset) {
        setDatasetNameInput(dataset.name);
      }
      setEditingDatasetId(null);
      return;
    }

    const dataset = getFromDatasetRecord(project.datasets, createDatasetId(datasetId));
    if (!dataset) {
      setEditingDatasetId(null);
      return;
    }

    if (trimmedName !== dataset.name) {
      try {
        logger.info('[ProjectOverviewTabContent] Updating dataset name', {
          datasetId,
          oldName: dataset.name,
          newName: trimmedName
        });

        // 依存するSQL Query Operationも更新するため非同期関数を使用
        updateDatasetName(createDatasetId(datasetId), trimmedName, duckdbProxy)
          .catch((error) => {
            logger.error('[ProjectOverviewTabContent] Failed to update dataset name:', error);
            if (error instanceof Error) {
              showError(error.message);
            }
          });
      } catch (error) {
        logger.error('[ProjectOverviewTabContent] Failed to update dataset name:', error);
        if (error instanceof Error) {
          showError(error.message);
        }
        setDatasetNameInput(dataset.name);
      }
    }
    setEditingDatasetId(null);
  }, [datasetNameInput, project, updateDatasetName, showError]);

  // データセット名の編集キャンセル
  const handleCancelEditDatasetName = useCallback(() => {
    setEditingDatasetId(null);
    setDatasetNameInput('');
  }, []);

  // データセット名編集のキーハンドラー
  const handleDatasetNameKeyDown = useCallback((datasetId: string, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSaveDatasetName(datasetId);
    } else if (event.key === 'Escape') {
      handleCancelEditDatasetName();
    }
  }, [handleSaveDatasetName, handleCancelEditDatasetName]);

  // メタデータモーダルの開閉ハンドラー
  const handleShowMetadata = useCallback((datasetId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setMetadataDatasetId(datasetId);
  }, []);

  const handleCloseMetadata = useCallback(() => {
    setMetadataDatasetId(null);
  }, []);

  // データセットの一覧を取得
  const datasets = useMemo(() => {
    if (!project?.datasets) return [];
    return Object.entries(project.datasets)
      .map(([id, dataset]) => ({
        id,
        name: dataset.name,
        rowCount: dataset.rowCount,
        columnCount: dataset.schema.columns.length,
        type: dataset.type === 'derived' ? 'derived' : 'original'
      }));
  }, [project?.datasets]);

  // レポートの一覧を取得
  const reports = useMemo(() => {
    if (!project?.reports) return [];
    // reportsがオブジェクトの場合は値を配列に変換
    const reportArray = Array.isArray(project.reports)
      ? project.reports
      : Object.values(project.reports);
    return reportArray.filter((report): report is Report => report !== null && typeof report === 'object').map((report) => ({
      id: report.id,
      name: report.name,
      elementCount: Object.keys(report.elements || {}).length,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    }));
  }, [project?.reports]);

  // モデルの一覧を取得
  const models = useMemo(() => {
    if (!project) return [];
    const modelManager = new ModelManager(project);
    return modelManager.getAllModels().map(model => ({
      id: model.id,
      name: model.name,
      type: model.type,
      features: model.metadata.features,
      target: model.metadata.target,
      createdAt: model.metadata.createdAt
    }));
  }, [project]);

  // データセットクリックハンドラー
  const handleDatasetClick = useCallback((datasetId: string) => {
    onOpenDataset?.(createDatasetId(datasetId));
  }, [onOpenDataset]);

  // レポートクリックハンドラー
  const handleReportClick = useCallback((reportId: string) => {
    onOpenReport?.(createReportId(reportId));
  }, [onOpenReport]);

  // モデルクリックハンドラー
  const handleModelClick = useCallback((modelId: string) => {
    onOpenModel?.(createModelId(modelId));
  }, [onOpenModel]);

  // データセット削除ハンドラー
  const handleDeleteDataset = useCallback(async (datasetId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    logger.info('[ProjectOverviewTabContent] Delete dataset button clicked:', { datasetId });

    if (!project) {
      logger.warn('[ProjectOverviewTabContent] Cannot delete dataset: missing project');
      return;
    }

    if (Object.keys(project.datasets).length <= 1) {
      logger.warn('[ProjectOverviewTabContent] Cannot delete last dataset');
      showError('Cannot delete the last dataset');
      return;
    }

    // 依存関係をチェック
    const datasetIdTyped = createDatasetId(datasetId);
    const dependencies = checkDatasetDependencies(project, datasetIdTyped);
    const dataset = getFromDatasetRecord(project.datasets, datasetIdTyped);
    const datasetName = dataset?.name || 'Unknown Dataset';

    logger.info('[ProjectOverviewTabContent] Dataset deletion check:', {
      datasetId,
      datasetName,
      hasAnyDependencies: dependencies.hasAnyDependencies,
      dependentModels: dependencies.models?.length || 0,
      dependentReports: dependencies.reports?.length || 0
    });

    let confirmMessage = `Are you sure you want to delete dataset "${datasetName}"?`;
    let confirmTitle = 'Delete Dataset';
    let confirmDetails: string | undefined;

    if (dependencies.hasAnyDependencies) {
      const warningMessage = generateDependencyWarningMessage(dependencies, datasetName);
      confirmMessage = warningMessage;
      confirmTitle = 'Delete Dataset with Dependencies';
    }

    const confirmed = await confirm({
      title: confirmTitle,
      message: confirmMessage,
      details: confirmDetails,
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      testId: 'delete-dataset-confirm'
    });

    if (!confirmed) {
      logger.info('[ProjectOverviewTabContent] Dataset deletion cancelled by user');
      return;
    }

    try {
      // データセットを削除
      logger.info('[ProjectOverviewTabContent] Deleting dataset:', { datasetId, datasetName });

      updateProject((proj) => {
        const newDatasets = { ...proj.datasets };
        delete newDatasets[datasetIdTyped];

        return {
          ...proj,
          datasets: newDatasets,
          metadata: {
            ...proj.metadata,
            lastModified: new Date().toISOString()
          }
        };
      });

      logger.info('[ProjectOverviewTabContent] Dataset deleted successfully:', { datasetId });
    } catch (error) {
      logger.error('[ProjectOverviewTabContent] Failed to delete dataset:', error);
      showError('Failed to delete dataset');
    }
  }, [project, updateProject, showError, confirm]);

  // Materialized Viewトグルハンドラー
  const handleToggleMaterialize = useCallback((datasetId: string, event: React.SyntheticEvent) => {
    event.stopPropagation();

    if (!project) return;

    const datasetIdTyped = createDatasetId(datasetId);
    const dataset = getFromDatasetRecord(project.datasets, datasetIdTyped);
    if (!dataset || dataset.type !== 'derived') return;

    const newMaterialized = !dataset.materialized;

    updateDataset(datasetIdTyped, (ds) => {
      if (ds.type !== 'derived') return ds;
      return {
        ...ds,
        materialized: newMaterialized
      };
    });

    logger.info('[ProjectOverviewTabContent] Toggled Materialized View:', {
      datasetId,
      datasetName: dataset.name,
      materialized: newMaterialized
    });
  }, [project, updateDataset]);

  // レポート削除ハンドラー
  const handleDeleteReport = useCallback(async (reportId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!project) return;

    const confirmed = await confirm({
      title: 'Delete Report',
      message: 'Are you sure you want to delete this report?',
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      testId: 'delete-report-confirm'
    });

    if (!confirmed) {
      return;
    }

    try {
      // reportsは常に配列として扱う
      const updatedReports = (project.reports || []).filter(report => report.id !== reportId);

      const updatedProject = {
        ...project,
        reports: updatedReports,
        metadata: {
          ...project.metadata,
          lastModified: new Date().toISOString()
        }
      };
      updateProject(() => updatedProject);
    } catch (error) {
      logger.error('Failed to delete report:', error);
      showError('Failed to delete report');
    }
  }, [project, updateProject, confirm, showError]);

  // モデル削除ハンドラー
  const handleDeleteModel = useCallback(async (modelId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    logger.info('[ProjectOverviewTabContent] Delete model button clicked:', { modelId });

    if (!project) {
      logger.warn('[ProjectOverviewTabContent] Cannot delete model: missing project');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Model',
      message: 'Are you sure you want to delete this model?',
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      testId: 'delete-model-confirm'
    });

    if (!confirmed) {
      logger.info('[ProjectOverviewTabContent] Model deletion cancelled by user');
      return;
    }

    try {
      logger.info('[ProjectOverviewTabContent] Starting model deletion:', { modelId });

      const model = project.models[createModelId(modelId)];
      if (!model) {
        logger.error('[ProjectOverviewTabContent] Model not found:', { modelId });
        showError('Failed to delete model: model not found');
        return;
      }

      logger.info('[ProjectOverviewTabContent] Model found:', {
        modelId,
        modelName: model.name,
        modelType: model.type
      });

      // イミュータブルに削除
      updateProject((proj) => {
        const newModels = { ...proj.models };
        delete newModels[createModelId(modelId)];

        // GLMモデルで診断データセットがある場合は自動削除
        if (model.type === 'glm') {
          const glmParameters = model.parameters as import('../../../../types/model.types').GLMParameters;
          if (glmParameters.diagnosticSummary?.diagnosticDatasetId) {
            const diagnosticDatasetId = glmParameters.diagnosticSummary.diagnosticDatasetId;
            logger.info('[ProjectOverviewTabContent] Also deleting diagnostic dataset:', { diagnosticDatasetId });

            const newDatasets = { ...proj.datasets };
            delete newDatasets[diagnosticDatasetId];

            return {
              ...proj,
              models: newModels,
              datasets: newDatasets,
              metadata: {
                ...proj.metadata,
                lastModified: new Date().toISOString()
              }
            };
          }
        }

        return {
          ...proj,
          models: newModels,
          metadata: {
            ...proj.metadata,
            lastModified: new Date().toISOString()
          }
        };
      });

      logger.info('[ProjectOverviewTabContent] Model deleted successfully');
    } catch (error) {
      logger.error('[ProjectOverviewTabContent] Failed to delete model:', error);
      showError('Failed to delete model');
    }
  }, [project, updateProject, confirm, showError]);

  // 外側クリックでメニューを閉じる
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuDatasetId) {
        setOpenMenuDatasetId(null);
      }
    };

    if (openMenuDatasetId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuDatasetId]);

  // 日付をフォーマット
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div
      className={styles.container}
    >
      <h2 className={styles.title}>Project Overview</h2>

      {/* プロジェクト名 */}
      <div className={styles.projectNameSection}>
        <label className={styles.projectNameLabel}>Project Name:</label>
        {isEditingProjectName ? (
          <div className={styles.projectNameEdit}>
            <input
              type="text"
              value={projectNameInput}
              onChange={(e) => setProjectNameInput(e.target.value)}
              onBlur={handleSaveProjectName}
              onKeyDown={handleProjectNameKeyDown}
              className={styles.projectNameInput}
              autoFocus
            />
            <button
              onClick={handleSaveProjectName}
              className={styles.projectNameButton}
              title="Save"
            >
              ✓
            </button>
            <button
              onClick={handleCancelEditProjectName}
              className={styles.projectNameButton}
              title="Cancel"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className={styles.projectNameDisplay} onClick={handleStartEditProjectName}>
            <span className={styles.projectNameText}>{project?.projectName}</span>
            <span className={styles.projectNameEditIcon}>✎</span>
          </div>
        )}
      </div>

      {/* データセットセクション */}
      <section className={styles.section} data-testid="datasets-section">
        <h3 className={styles.sectionTitle}>Datasets ({datasets.length})</h3>
        <div className={styles.itemList}>
          {datasets.length === 0 ? (
            <p className={styles.emptyMessage}>No datasets loaded</p>
          ) : (
            datasets.map(dataset => (
              <div
                key={dataset.id}
                className={styles.item}
                onClick={() => editingDatasetId !== dataset.id && openMenuDatasetId !== dataset.id && handleDatasetClick(dataset.id)}
              >
                <div className={styles.itemHeader}>
                  {editingDatasetId === dataset.id ? (
                    <div className={styles.projectNameEdit} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={datasetNameInput}
                        onChange={(e) => setDatasetNameInput(e.target.value)}
                        onBlur={() => handleSaveDatasetName(dataset.id)}
                        onKeyDown={(e) => handleDatasetNameKeyDown(dataset.id, e)}
                        className={styles.projectNameInput}
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveDatasetName(dataset.id)}
                        className={styles.projectNameButton}
                        title="Save"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelEditDatasetName}
                        className={styles.projectNameButton}
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={styles.itemName} data-testid={`dataset-name-${dataset.id}`}>
                        {dataset.name}
                      </span>
                      {dataset.type === 'derived' && (
                        <>
                          <span className={styles.badge}>Derived</span>
                          {/* SQLクエリで作成されたデータセットの場合、SQLバッジを表示 */}
                          {project && (() => {
                            const fullDataset = getFromDatasetRecord(project.datasets, createDatasetId(dataset.id));
                            if (fullDataset?.type === 'derived') {
                              const derived = fullDataset as DerivedDataSet;
                              return derived.operation?.type === 'sql_query' ? (
                                <span className={styles.badge} style={{ backgroundColor: '#4a90e2' }}>SQL</span>
                              ) : null;
                            }
                            return null;
                          })()}
                        </>
                      )}
                    </>
                  )}
                  <div className={styles.rightActions}>
                    <button
                      className={styles.infoButton}
                      onClick={(e) => handleShowMetadata(dataset.id, e)}
                      title="View dataset metadata"
                      aria-label="View dataset metadata"
                      data-testid="dataset-metadata-button"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M8 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
                      </svg>
                    </button>
                    <div className={styles.menuContainer}>
                      <button
                        className={styles.menuButton}
                        onClick={(e) => handleToggleMenu(dataset.id, e)}
                        title="Dataset options"
                        aria-label="Dataset menu"
                      >
                        ⋮
                      </button>
                    {openMenuDatasetId === dataset.id && (
                      <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
                        <button
                          className={styles.menuItem}
                          onClick={() => handleStartEditDatasetName(dataset.id, dataset.name)}
                        >
                          Rename
                        </button>
                        <button
                          className={styles.menuItem}
                          onClick={(e) => {
                            handleCloseMenu();
                            handleDeleteDataset(dataset.id, e);
                          }}
                        >
                          Delete
                        </button>
                        {dataset.type === 'derived' && project && (() => {
                          // 最新のdatasetを取得（projectストアから）
                          const currentDataset = getFromDatasetRecord(project.datasets, createDatasetId(dataset.id));
                          if (!currentDataset || currentDataset.type !== 'derived') return null;

                          const isMaterialized = currentDataset.materialized === true;
                          return (
                            <>
                              <div className={styles.divider} />
                              <label
                                className={styles.menuItem}
                                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                data-testid="toggle-materialize-overview"
                              >
                                <input
                                  type="checkbox"
                                  checked={isMaterialized}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggleMaterialize(dataset.id, e);
                                  }}
                                  style={{ marginRight: '8px' }}
                                />
                                💾 Save data with project (Materialized View)
                              </label>
                            </>
                          );
                        })()}
                        <div className={styles.divider} />
                        <button
                          className={`${styles.menuItem} ${styles.disabled}`}
                          disabled
                          title="Coming soon"
                        >
                          Export...
                        </button>
                      </div>
                    )}
                    </div>
                  </div>
                </div>
                <div className={styles.itemDetails}>
                  <span>{dataset.rowCount} rows × {dataset.columnCount} columns</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* レポートセクション */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Reports ({reports.length})</h3>
        <div className={styles.itemList}>
          {reports.length === 0 ? (
            <p className={styles.emptyMessage}>No reports created</p>
          ) : (
            reports.map(report => (
              <div
                key={report.id}
                className={styles.item}
                onClick={() => handleReportClick(report.id)}
              >
                <div className={styles.itemHeader}>
                  <span className={styles.itemName}>{report.name}</span>
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => handleDeleteReport(report.id, e)}
                    title="Delete report"
                  >
                    ×
                  </button>
                </div>
                <div className={styles.itemDetails}>
                  <span>{report.elementCount} elements</span>
                  <span className={styles.separator}>•</span>
                  <span>Modified: {formatDate(report.lastModified)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* モデルセクション */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Models ({models.length})</h3>
        <div className={styles.itemList}>
          {models.length === 0 ? (
            <p className={styles.emptyMessage}>No models saved</p>
          ) : (
            models.map(model => (
              <div
                key={model.id}
                className={styles.item}
                onClick={() => handleModelClick(model.id)}
              >
                <div className={styles.itemHeader}>
                  <span className={styles.itemName}>{model.name}</span>
                  <span className={styles.badge}>{model.type.replace('_', ' ')}</span>
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => handleDeleteModel(model.id, e)}
                    title="Delete model"
                  >
                    ×
                  </button>
                </div>
                <div className={styles.itemDetails}>
                  <span>Features: {model.features.join(', ')}</span>
                  <span className={styles.separator}>•</span>
                  <span>Target: {model.target}</span>
                </div>
                <div className={styles.itemDetails}>
                  <span>Created: {formatDate(model.createdAt)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* メタデータモーダル */}
      {metadataDatasetId && project && (() => {
        const dataset = getFromDatasetRecord(project.datasets, createDatasetId(metadataDatasetId));
        return dataset ? (
          <DatasetMetadataModal
            dataset={dataset}
            onClose={handleCloseMetadata}
          />
        ) : null;
      })()}
    </div>
  );
};